import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import {
  Box, Typography, Paper, Grid, Button, TextField, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  Select, MenuItem, FormControl, CircularProgress,
  Tabs, Tab, Chip, Checkbox,
  TablePagination, Snackbar, Autocomplete,
  ToggleButtonGroup, ToggleButton,
} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import BlockIcon from "@mui/icons-material/Block";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import BusinessIcon from "@mui/icons-material/Business";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  fetchPartner, editPartner, changePartnerStatus,
  fetchPartnerCompanies, addCompanyToPartner,
  fetchPartnerUsers, addPartnerUser, editPartnerUser,
  regeneratePartnerUserInvite, fetchPartnerActionLogs,
  reassignPartnerCompanies,
} from "../redux/partners.action";
import { resetPartner } from "../redux/partners.slice";
import { getCompaniesDropdown } from "app/api/companies.api";
import { unassignCompany, getPartners } from "app/api/partners.api";
import { getRoles } from "app/api/access-rights.api";

const ACTION_LABELS = {
  credit_transfer: "Credit Transfer",
  notification_recipient_added: "Recipient Added",
  notification_recipient_removed: "Recipient Removed",
};

// Each action type fills the generic log columns with its own meaningful data.
const isNotificationAction = (action) =>
  action === "notification_recipient_added" || action === "notification_recipient_removed";

const companyLabelOf = (log) => {
  if (log.company_name) return log.company_name;
  if (log.company_id) return `Company #${log.company_id}`;
  if (log.details?.scope === "all_companies") return "All companies";
  return "-";
};

const targetOf = (log) => {
  if (isNotificationAction(log.action)) {
    const email = log.details?.recipient_email || "";
    const name = log.details?.recipient_name || "";
    return { name: name || email || "-", email: name ? email : "" };
  }
  return {
    name: log.target_user_name || log.target_user_email || "-",
    email: log.target_user_email || "",
  };
};

const STATUS_PENDING = 1;
const STATUS_ACTIVE = 2;
const STATUS_INACTIVE = 3;

const statusLabels = { 1: "Pending", 2: "Active", 3: "Inactive" };
const statusColors = { 1: "#F79009", 2: "#12B76A", 3: "#F04438" };

// Partner-level status wording ("Deactivated", not "Inactive"). Pending exists
// only for legacy rows — it can no longer be set.
const partnerStatusLabels = { 1: "Pending", 2: "Active", 3: "Deactivated" };

// Company status comes from the account DB (pending / suspended / rejected /
// approved / deactivated). Only approved and deactivated get a colour.
const companyStatusStyle = (status) => {
  if (status === "approved") return { backgroundColor: "#ECFDF3", color: "#067647" };
  if (status === "deactivated") return { backgroundColor: "#FEF3F2", color: "#B42318" };
  return { backgroundColor: "#F2F4F7", color: "#667085" };
};
const companyStatusLabel = (status) =>
  status ? status.charAt(0).toUpperCase() + status.slice(1) : "Unknown";

const PASSWORD_REGEX =
  /^(?=.{8,}$)(?=.*?[a-z])(?=.*?[A-Z])(?=.*?[0-9])(?=.*[!@#$%^&~`*()_+\-={}[\]:";'<>,.?|]).*$/;

const useStyles = makeStyles(() => ({
  backButton: {
    marginBottom: 16,
    color: "#3A4D54",
    fontWeight: 700,
    fontSize: 13,
    textTransform: "uppercase",
  },
  card: {
    padding: 24,
    marginBottom: 24,
    border: "1px solid #D0D5DD",
    borderRadius: 12,
    boxShadow: "0px 2px 10px 0px rgba(124,141,181,0.12)",
  },
  tabsCard: {
    padding: 0,
    border: "1px solid #D0D5DD",
    borderRadius: 12,
    boxShadow: "0px 2px 10px 0px rgba(124,141,181,0.12)",
    overflow: "hidden",
  },
  partnerName: {
    fontWeight: 700,
    fontSize: 18,
    color: "#0F151E",
    lineHeight: "24px",
  },
  editBtn: {
    border: "1px solid #3A4D54",
    color: "#3A4D54",
    fontWeight: 700,
    fontSize: 14,
    padding: "6px 16px",
    "&:hover": {
      backgroundColor: "#F9FAFB",
    },
  },
  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "3px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: "18px",
  },
  statusBadgeDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    display: "inline-block",
  },
  statusActive: {
    backgroundColor: "#ECFDF3",
    color: "#067647",
    "& $statusBadgeDot": { backgroundColor: "#17B26A" },
  },
  statusDeactivated: {
    backgroundColor: "#FEF3F2",
    color: "#B42318",
    "& $statusBadgeDot": { backgroundColor: "#F04438" },
  },
  statusPending: {
    backgroundColor: "#FFFAEB",
    color: "#B54708",
    "& $statusBadgeDot": { backgroundColor: "#F79009" },
  },
  deactivateBtn: {
    border: "1px solid #FDA29B",
    color: "#B42318",
    fontWeight: 700,
    fontSize: 14,
    padding: "6px 16px",
    transition: "background-color 0.2s ease, border-color 0.2s ease",
    "&:hover": {
      backgroundColor: "#FEF3F2",
      borderColor: "#F04438",
    },
  },
  activateBtn: {
    backgroundColor: "#067647",
    color: "#fff",
    fontWeight: 700,
    fontSize: 14,
    padding: "6px 16px",
    boxShadow: "none",
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: "#05603A",
      boxShadow: "none",
    },
  },
  deactivatedBanner: {
    marginTop: 20,
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 14px",
    borderRadius: 8,
    backgroundColor: "#FEF3F2",
    border: "1px solid #FECDCA",
    color: "#B42318",
  },
  label: {
    color: "#7C8DB5",
    fontSize: 14,
    fontWeight: 400,
    marginBottom: 4,
    lineHeight: "20px",
  },
  value: {
    color: "#0F151E",
    fontSize: 14,
    fontWeight: 500,
    lineHeight: "20px",
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: 14,
    color: "#0F151E",
    lineHeight: "20px",
  },
  assignBtn: {
    border: "1px solid #3A4D54",
    color: "#3A4D54",
    fontWeight: 700,
    fontSize: 14,
    padding: "6px 16px",
    "&:hover": {
      backgroundColor: "#F9FAFB",
    },
  },
  tableContainer: {
    border: "none",
    borderRadius: 0,
    boxShadow: "none",
  },
  deleteBtn: {
    color: "#FD646F",
  },
  tabs: {
    borderBottom: "1px solid #D0D5DD",
    backgroundColor: "#F9FAFB",
  },
  tab: {
    textTransform: "none",
    fontWeight: 600,
    fontSize: 14,
    color: "#667085",
    minWidth: 120,
    "&.Mui-selected": {
      color: "#3A4D54",
      fontWeight: 700,
    },
  },
  tabIndicator: {
    backgroundColor: "#3A4D54",
    height: 3,
  },
  tabContent: {
    padding: 24,
  },
  accessChip: {
    marginRight: 6,
    marginBottom: 4,
    fontSize: 12,
    height: 24,
    backgroundColor: "#EEF4FF",
    color: "#3538CD",
    fontWeight: 500,
  },
  statusChip: {
    fontSize: 12,
    height: 24,
    fontWeight: 600,
  },
}));

export default function PartnerDetailPage() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { uuid } = useParams();
  const {
    currentPartner, partnerCompanies, partnerCompaniesTotal, isLoadingCompanies,
    partnerUsers, isLoading,
    partnerLogs, partnerLogsTotal, isLoadingLogs,
  } = useSelector((state) => state.partners);

  const [activeTab, setActiveTab] = useState(0);
  const [companiesPage, setCompaniesPage] = useState(0);
  const [companiesPerPage, setCompaniesPerPage] = useState(25);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [assignOpen, setAssignOpen] = useState(false);
  const [companyDropdown, setCompanyDropdown] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [assignError, setAssignError] = useState("");
  const [moveConfirmOpen, setMoveConfirmOpen] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);

  // Logs tab state
  const [logsPage, setLogsPage] = useState(0);
  const [logsPerPage, setLogsPerPage] = useState(25);
  const [logsSearch, setLogsSearch] = useState("");
  const [debouncedLogsSearch, setDebouncedLogsSearch] = useState("");
  const [logsView, setLogsView] = useState("credit_transfer");

  // Reassign-before-deactivate dialog state
  const [reassignOpen, setReassignOpen] = useState(false);
  const [partnerOptions, setPartnerOptions] = useState([]);
  const [targetPartnerUuid, setTargetPartnerUuid] = useState("");
  const [reassignError, setReassignError] = useState("");
  const [isReassigning, setIsReassigning] = useState(false);

  // Deactivate/Activate state
  const [confirmDeactivateOpen, setConfirmDeactivateOpen] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [statusSnack, setStatusSnack] = useState("");

  // User dialog state
  const [roleOptions, setRoleOptions] = useState([]);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [userDialogMode, setUserDialogMode] = useState("add"); // "add" or "edit"
  const [userForm, setUserForm] = useState({ name: "", email: "", password: "", roleUuids: [] });
  const [editingUserUuid, setEditingUserUuid] = useState(null);
  const [editingUserStatus, setEditingUserStatus] = useState(null);
  const [activationUrl, setActivationUrl] = useState("");
  const [activationExpiresAt, setActivationExpiresAt] = useState("");
  const [userFormError, setUserFormError] = useState("");
  const [copyTooltip, setCopyTooltip] = useState("");

  useEffect(() => {
    dispatch(fetchPartner(uuid));
    return () => dispatch(resetPartner());
  }, [dispatch, uuid]);

  // Companies are paged server-side; every page/size change is a new request.
  useEffect(() => {
    dispatch(fetchPartnerCompanies(uuid, { page: companiesPage + 1, length: companiesPerPage }));
  }, [dispatch, uuid, companiesPage, companiesPerPage]);

  // Assign/unassign can move the current page out of range — clamp back onto it
  // (the page change above then refetches).
  useEffect(() => {
    if (companiesPage > 0 && companiesPage * companiesPerPage >= partnerCompaniesTotal) {
      setCompaniesPage(Math.max(0, Math.ceil(partnerCompaniesTotal / companiesPerPage) - 1));
    }
  }, [partnerCompaniesTotal, companiesPerPage, companiesPage]);

  const reloadCompanies = () =>
    dispatch(fetchPartnerCompanies(uuid, { page: companiesPage + 1, length: companiesPerPage }));

  useEffect(() => {
    if (currentPartner?.id) {
      dispatch(fetchPartnerUsers(currentPartner.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, currentPartner && currentPartner.id]);

  // Roles are global — load once for the Add/Edit User picker.
  useEffect(() => {
    getRoles()
      .then((res) => setRoleOptions(res.data.data || []))
      .catch(() => setRoleOptions([]));
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedLogsSearch(logsSearch), 400);
    return () => clearTimeout(timer);
  }, [logsSearch]);

  useEffect(() => {
    setLogsPage(0);
  }, [debouncedLogsSearch, logsView]);

  useEffect(() => {
    if (activeTab !== 2) return;
    const params = {
      page: logsPage + 1,
      length: logsPerPage,
      action: logsView === "credit_transfer"
        ? "credit_transfer"
        : "notification_recipient_added,notification_recipient_removed",
    };
    if (debouncedLogsSearch) params.search = debouncedLogsSearch;
    dispatch(fetchPartnerActionLogs(uuid, params));
  }, [dispatch, uuid, activeTab, logsPage, logsPerPage, debouncedLogsSearch, logsView]);

  const handleEdit = async () => {
    const result = await dispatch(editPartner(uuid, editForm));
    if (result.success) {
      setEditOpen(false);
      dispatch(fetchPartner(uuid));
    }
  };

  const handleDeactivateClick = () => {
    // A partner with assigned companies cannot be deactivated — prompt the admin
    // to move the companies to another partner first.
    if (partnerCompaniesTotal > 0) {
      openReassignDialog();
      return;
    }
    setConfirmDeactivateOpen(true);
  };

  const handleConfirmDeactivate = async () => {
    setIsUpdatingStatus(true);
    const result = await dispatch(changePartnerStatus(uuid, STATUS_INACTIVE));
    setIsUpdatingStatus(false);
    setConfirmDeactivateOpen(false);
    if (result && !result.success) {
      alert(result.message || "Failed to deactivate partner");
    } else {
      setStatusSnack("Partner deactivated.");
    }
    dispatch(fetchPartner(uuid));
  };

  const handleActivate = async () => {
    setIsUpdatingStatus(true);
    const result = await dispatch(changePartnerStatus(uuid, STATUS_ACTIVE));
    setIsUpdatingStatus(false);
    if (result && !result.success) {
      alert(result.message || "Failed to activate partner");
    } else {
      setStatusSnack("Partner activated.");
    }
    dispatch(fetchPartner(uuid));
  };

  const openReassignDialog = async () => {
    setReassignError("");
    setTargetPartnerUuid("");
    try {
      const res = await getPartners({ status: STATUS_ACTIVE, length: 100 });
      const options = (res.data.data?.data || []).filter((p) => p.uuid !== uuid);
      setPartnerOptions(options);
    } catch (e) {
      setPartnerOptions([]);
    }
    setReassignOpen(true);
  };

  const handleReassignAndDeactivate = async () => {
    if (!targetPartnerUuid) return;
    setIsReassigning(true);
    setReassignError("");

    const reassignResult = await dispatch(reassignPartnerCompanies(uuid, {
      target_partner_uuid: targetPartnerUuid,
    }));
    if (!reassignResult.success) {
      setReassignError(reassignResult.message || "Failed to reassign companies.");
      setIsReassigning(false);
      return;
    }

    const statusResult = await dispatch(changePartnerStatus(uuid, STATUS_INACTIVE));
    setIsReassigning(false);
    if (!statusResult.success) {
      setReassignError(statusResult.message || "Companies were reassigned, but deactivation failed.");
      reloadCompanies();
      return;
    }

    setReassignOpen(false);
    setStatusSnack("Companies reassigned and partner deactivated.");
    dispatch(fetchPartner(uuid));
    reloadCompanies();
  };

  const openAssignDialog = async () => {
    setSelectedCompanies([]);
    setAssignError("");
    try {
      const res = await getCompaniesDropdown();
      setCompanyDropdown(res.data.data || []);
    } catch (e) { /* ignore */ }
    setAssignOpen(true);
  };

  const heldSelections = selectedCompanies.filter((c) => c.assigned_partner);

  // With server-side paging only the current page is in memory, so lean on the
  // dropdown's own assigned_partner label; the page check covers legacy rows
  // where a company sits under several partners.
  const isAssignedHere = (option) =>
    option.assigned_partner?.id === currentPartner?.id
    || partnerCompanies.some((pc) => pc.company_id === option.id);

  const handleAssignClick = () => {
    if (selectedCompanies.length === 0) return;
    setAssignError("");
    // Any selection held by another partner — confirm the move before transferring.
    if (heldSelections.length > 0) {
      setMoveConfirmOpen(true);
      return;
    }
    doAssign(false);
  };

  const doAssign = async (move) => {
    setIsAssigning(true);
    const result = await dispatch(addCompanyToPartner(uuid, {
      company_ids: selectedCompanies.map((c) => c.id),
      move,
    }));
    setIsAssigning(false);
    setMoveConfirmOpen(false);
    if (!result.success) {
      setAssignError(result.message || "Failed to assign companies.");
      return;
    }
    const assignedCount = result.data?.assigned ?? selectedCompanies.length;
    const movedCount = result.data?.moved ?? 0;
    setAssignOpen(false);
    setSelectedCompanies([]);
    setStatusSnack(
      `${assignedCount} ${assignedCount === 1 ? "company" : "companies"} assigned`
      + (movedCount > 0 ? ` (${movedCount} moved from another partner)` : "")
      + "."
    );
    reloadCompanies();
  };

  const handleUnassign = async (assignmentId) => {
    try {
      await unassignCompany(uuid, assignmentId);
      reloadCompanies();
    } catch (e) {
      alert(e.response?.data?.message || "Failed to remove company");
    }
  };

  // User handlers
  const resetUserDialog = () => {
    setUserForm({ name: "", email: "", password: "", roleUuids: [] });
    setEditingUserUuid(null);
    setEditingUserStatus(null);
    setActivationUrl("");
    setActivationExpiresAt("");
    setUserFormError("");
    setCopyTooltip("");
  };

  const openAddUserDialog = () => {
    setUserDialogMode("add");
    resetUserDialog();
    setUserDialogOpen(true);
  };

  const openEditUserDialog = (user) => {
    setUserDialogMode("edit");
    resetUserDialog();
    setUserForm({
      name: user.name || "",
      email: user.email || "",
      password: "",
      roleUuids: (user.roles || []).map((r) => r.uuid),
    });
    setEditingUserUuid(user.uuid);
    setEditingUserStatus(user.status);
    setUserDialogOpen(true);
  };

  const closeUserDialog = () => {
    setUserDialogOpen(false);
  };

  const copyActivationUrl = async () => {
    try {
      await navigator.clipboard.writeText(activationUrl);
      setCopyTooltip("Copied!");
      setTimeout(() => setCopyTooltip(""), 2000);
    } catch (e) {
      setCopyTooltip("Copy failed — select the URL manually.");
    }
  };

  const handleRegenerateInvite = async () => {
    if (!editingUserUuid) return;
    const result = await dispatch(regeneratePartnerUserInvite(editingUserUuid));
    if (result.success) {
      setActivationUrl(result.data.activation_url);
      setActivationExpiresAt(result.data.activation_expires_at);
      setUserFormError("");
      dispatch(fetchPartnerUsers(currentPartner.id));
    } else {
      setUserFormError(result.message || "Failed to regenerate invitation.");
    }
  };

  const handleUserSubmit = async () => {
    setUserFormError("");
    if (userDialogMode === "add") {
      const result = await dispatch(addPartnerUser({
        name: userForm.name,
        email: userForm.email,
        partner_id: currentPartner.id,
        role_uuids: userForm.roleUuids,
      }));
      if (result.success) {
        setActivationUrl(result.data.activation_url);
        setActivationExpiresAt(result.data.activation_expires_at);
        dispatch(fetchPartnerUsers(currentPartner.id));
      } else {
        setUserFormError(result.message || "Failed to create user.");
      }
    } else {
      if (userForm.password && !PASSWORD_REGEX.test(userForm.password)) {
        setUserFormError("Password must be at least 8 characters with lowercase, uppercase, digit and special character.");
        return;
      }
      const data = { name: userForm.name, role_uuids: userForm.roleUuids };
      if (editingUserStatus === STATUS_PENDING) {
        data.email = userForm.email;
      }
      if (userForm.password) data.password = userForm.password;
      const result = await dispatch(editPartnerUser(editingUserUuid, data));
      if (result.success) {
        setUserDialogOpen(false);
        dispatch(fetchPartnerUsers(currentPartner.id));
      } else {
        setUserFormError(result.message || "Failed to update user.");
      }
    }
  };

  if (isLoading || !currentPartner) {
    return <Box display="flex" justifyContent="center" p={4}><CircularProgress style={{ color: "#3A4D54" }} /></Box>;
  }

  const isDeactivated = currentPartner.status === STATUS_INACTIVE;
  const partnerStatusClass = currentPartner.status === STATUS_ACTIVE
    ? classes.statusActive
    : isDeactivated ? classes.statusDeactivated : classes.statusPending;

  return (
    <Box>
      <Button className={classes.backButton} startIcon={<ArrowBackIcon />} onClick={() => history.push("/admin/partners")}>
        Back to Partners
      </Button>
      {/* Partner Info Card */}
      <Paper className={classes.card} elevation={0}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center" style={{ gap: 12 }}>
            <Typography className={classes.partnerName}>{currentPartner.name}</Typography>
            <span className={`${classes.statusBadge} ${partnerStatusClass}`}>
              <span className={classes.statusBadgeDot} />
              {partnerStatusLabels[currentPartner.status] || "Unknown"}
            </span>
          </Box>
          <Box display="flex" alignItems="center" style={{ gap: 10 }}>
            <Button
              variant="outlined"
              className={classes.editBtn}
              startIcon={<EditIcon style={{ fontSize: 16 }} />}
              onClick={() => { setEditForm(currentPartner); setEditOpen(true); }}
            >
              Edit
            </Button>
            {isDeactivated ? (
              <Button
                variant="contained"
                className={classes.activateBtn}
                startIcon={<PowerSettingsNewIcon style={{ fontSize: 16 }} />}
                onClick={handleActivate}
                disabled={isUpdatingStatus}
              >
                Activate
              </Button>
            ) : (
              <Button
                variant="outlined"
                className={classes.deactivateBtn}
                startIcon={<BlockIcon style={{ fontSize: 16 }} />}
                onClick={handleDeactivateClick}
                disabled={isUpdatingStatus}
              >
                Deactivate
              </Button>
            )}
          </Box>
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={4}>
            <Typography className={classes.label}>Email</Typography>
            <Typography className={classes.value}>{currentPartner.contact_email || "-"}</Typography>
          </Grid>
        </Grid>
        {isDeactivated && (
          <Box className={classes.deactivatedBanner}>
            <BlockIcon style={{ fontSize: 16 }} />
            <Typography style={{ fontSize: 13, fontWeight: 500, color: "inherit" }}>
              This partner is deactivated. Its portal users cannot sign in, and assigning companies or adding users is disabled.
            </Typography>
          </Box>
        )}
      </Paper>
      {/* Tabs Section */}
      <Paper className={classes.tabsCard} elevation={0}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          className={classes.tabs}
          classes={{ indicator: classes.tabIndicator }}
        >
          <Tab label="Companies" className={classes.tab} />
          <Tab label="Users" className={classes.tab} />
          <Tab label="Logs" className={classes.tab} />
        </Tabs>

        {/* Companies Tab */}
        {activeTab === 0 && (
          <Box className={classes.tabContent}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography className={classes.sectionTitle}>
                Assigned Companies ({partnerCompaniesTotal})
              </Typography>
              <Button
                variant="outlined"
                className={classes.assignBtn}
                startIcon={<AddIcon />}
                onClick={openAssignDialog}
                disabled={isDeactivated}
              >
                Assign Company
              </Button>
            </Box>
            <TableContainer component={Paper} className={classes.tableContainer} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Company Name</TableCell>
                    <TableCell>Company ID</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoadingCompanies && partnerCompanies.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" style={{ padding: 28 }}>
                        <CircularProgress size={22} style={{ color: "#3A4D54" }} />
                      </TableCell>
                    </TableRow>
                  ) : partnerCompanies.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" style={{ color: "#98A2B3" }}>No companies assigned</TableCell>
                    </TableRow>
                  ) : (
                    partnerCompanies.map((pc) => (
                      <TableRow key={pc.id}>
                        <TableCell>{pc.company_name || "N/A"}</TableCell>
                        <TableCell>{pc.company_id}</TableCell>
                        <TableCell>
                          <Chip
                            label={companyStatusLabel(pc.company_status)}
                            size="small"
                            className={classes.statusChip}
                            style={companyStatusStyle(pc.company_status)}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" className={classes.deleteBtn} onClick={() => handleUnassign(pc.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              {partnerCompaniesTotal > 0 && (
                <TablePagination
                  component="div"
                  count={partnerCompaniesTotal}
                  page={companiesPage}
                  rowsPerPage={companiesPerPage}
                  rowsPerPageOptions={[10, 25, 50]}
                  onPageChange={(e, newPage) => setCompaniesPage(newPage)}
                  onRowsPerPageChange={(e) => {
                    setCompaniesPerPage(parseInt(e.target.value, 10));
                    setCompaniesPage(0);
                  }}
                />
              )}
            </TableContainer>
          </Box>
        )}

        {/* Users Tab */}
        {activeTab === 1 && (
          <Box className={classes.tabContent}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography className={classes.sectionTitle}>
                Users ({partnerUsers.length})
              </Typography>
              <Button
                variant="outlined"
                className={classes.assignBtn}
                startIcon={<AddIcon />}
                onClick={openAddUserDialog}
                disabled={isDeactivated}
              >
                Add User
              </Button>
            </Box>
            <TableContainer component={Paper} className={classes.tableContainer} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Access</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {partnerUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" style={{ color: "#98A2B3" }}>No users found</TableCell>
                    </TableRow>
                  ) : (
                    partnerUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>{user.name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          {(user.roles || []).map((role) => (
                            <Chip key={role.uuid} label={role.name} className={classes.accessChip} size="small" />
                          ))}
                          {(!user.roles || user.roles.length === 0) && (
                            <Typography style={{ color: "#98A2B3", fontSize: 13 }}>-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={statusLabels[user.status] || "Unknown"}
                            className={classes.statusChip}
                            size="small"
                            style={{
                              backgroundColor: `${statusColors[user.status] || "#667085"}20`,
                              color: statusColors[user.status] || "#667085",
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => openEditUserDialog(user)} style={{ color: "#3A4D54", marginRight: 4 }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Logs Tab */}
        {activeTab === 2 && (
          <Box className={classes.tabContent}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
              <Typography className={classes.sectionTitle}>
                {logsView === "credit_transfer" ? "Credit Transfers" : "Notification Changes"} ({partnerLogsTotal})
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <ToggleButtonGroup
                  value={logsView}
                  exclusive
                  size="small"
                  onChange={(e, value) => value && setLogsView(value)}
                >
                  <ToggleButton value="credit_transfer" style={{ textTransform: "none", fontSize: 13, padding: "4px 14px" }}>
                    Credit Transfers
                  </ToggleButton>
                  <ToggleButton value="notifications" style={{ textTransform: "none", fontSize: 13, padding: "4px 14px" }}>
                    Notification Changes
                  </ToggleButton>
                </ToggleButtonGroup>
                <TextField
                  variant="outlined"
                  size="small"
                  placeholder="Search company or user"
                  value={logsSearch}
                  onChange={(e) => setLogsSearch(e.target.value)}
                  style={{ width: 240 }}
                  inputProps={{ style: { fontSize: 14 } }}
                />
              </Box>
            </Box>
            <TableContainer component={Paper} className={classes.tableContainer} elevation={0}>
              <Table size="small">
                <TableHead>
                  {logsView === "credit_transfer" ? (
                    <TableRow>
                      <TableCell>Date &amp; Time</TableCell>
                      <TableCell>Company</TableCell>
                      <TableCell>Performed By</TableCell>
                      <TableCell>Recipient</TableCell>
                      <TableCell align="right">Credits</TableCell>
                    </TableRow>
                  ) : (
                    <TableRow>
                      <TableCell>Date &amp; Time</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Performed By</TableCell>
                      <TableCell>Notification Recipient</TableCell>
                    </TableRow>
                  )}
                </TableHead>
                <TableBody>
                  {isLoadingLogs ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" style={{ padding: 28 }}>
                        <CircularProgress size={22} style={{ color: "#3A4D54" }} />
                      </TableCell>
                    </TableRow>
                  ) : partnerLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" style={{ color: "#98A2B3" }}>
                        {logsSearch
                          ? "No logs match your search"
                          : logsView === "credit_transfer" ? "No credit transfers yet" : "No notification changes yet"}
                      </TableCell>
                    </TableRow>
                  ) : (
                    partnerLogs.map((log) => {
                      const created = new Date(log.created_at);
                      const isDebit = log.details?.transfer_action === "debit";
                      const amount = log.amount !== null && log.amount !== undefined
                        ? parseFloat(log.amount)
                        : null;
                      const target = targetOf(log);
                      const dateCell = (
                        <TableCell style={{ whiteSpace: "nowrap" }}>
                          <Typography style={{ fontSize: 14, color: "#0F151E" }}>
                            {created.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}
                          </Typography>
                          <Typography style={{ fontSize: 12, color: "#7C8DB5" }}>
                            {created.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                          </Typography>
                        </TableCell>
                      );
                      const actorCell = (
                        <TableCell>
                          <Typography style={{ fontSize: 14, color: "#0F151E" }}>
                            {log.actor_name || "-"}
                          </Typography>
                          <Typography style={{ fontSize: 12, color: "#7C8DB5" }}>
                            {log.actor_email}
                          </Typography>
                        </TableCell>
                      );
                      const targetCell = (
                        <TableCell>
                          <Typography style={{ fontSize: 14, color: "#0F151E" }}>{target.name}</Typography>
                          <Typography style={{ fontSize: 12, color: "#7C8DB5" }}>{target.email}</Typography>
                        </TableCell>
                      );

                      if (logsView === "credit_transfer") {
                        return (
                          <TableRow key={log.uuid || log.id}>
                            {dateCell}
                            <TableCell>{companyLabelOf(log)}</TableCell>
                            {actorCell}
                            {targetCell}
                            <TableCell align="right" style={{ whiteSpace: "nowrap" }}>
                              {amount !== null ? (
                                <span style={{ fontWeight: 600, color: isDebit ? "#B42318" : "#067647" }}>
                                  {isDebit ? "− " : "+ "}{amount.toLocaleString()}
                                </span>
                              ) : (
                                "-"
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      }

                      return (
                        <TableRow key={log.uuid || log.id}>
                          {dateCell}
                          <TableCell>
                            <Chip
                              label={ACTION_LABELS[log.action] || log.action}
                              className={classes.accessChip}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{log.details?.scope || companyLabelOf(log)}</TableCell>
                          {actorCell}
                          {targetCell}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={partnerLogsTotal}
              page={logsPage}
              rowsPerPage={logsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
              onPageChange={(e, newPage) => setLogsPage(newPage)}
              onRowsPerPageChange={(e) => {
                setLogsPerPage(parseInt(e.target.value, 10));
                setLogsPage(0);
              }}
            />
          </Box>
        )}
      </Paper>
      {/* Edit Partner Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography style={{ fontWeight: 700, fontSize: 14, color: "#3A4D54" }}>Edit Partner</Typography>
            <IconButton size="small" onClick={() => setEditOpen(false)}><CloseIcon style={{ fontSize: 16 }} /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box bgcolor="#fff" borderRadius="4px" px="10px" py="20px">
            <Box display="flex" flexDirection="column" style={{ gap: 20 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography style={{ fontSize: 14, color: "#344054" }}>Name <span style={{ color: "#F97066" }}>*</span></Typography>
                <TextField variant="outlined" size="small" placeholder="Partner name" value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} style={{ width: 360 }} inputProps={{ style: { fontSize: 14, height: 18, padding: "10px" } }} />
              </Box>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography style={{ fontSize: 14, color: "#344054" }}>Email <span style={{ color: "#F97066" }}>*</span></Typography>
                <TextField variant="outlined" size="small" placeholder="Partner email" value={editForm.contact_email || ""} onChange={(e) => setEditForm({ ...editForm, contact_email: e.target.value })} style={{ width: 360 }} inputProps={{ style: { fontSize: 14, height: 18, padding: "10px" } }} />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEdit} variant="contained" style={{ backgroundColor: "#3A4D54", color: "#fff", fontWeight: 700, fontSize: 13, minWidth: 156, height: 32, borderRadius: 4, boxShadow: "0px 2px 2px rgba(34,45,48,0.15)" }}>CONFIRM</Button>
        </DialogActions>
      </Dialog>
      {/* Assign Company Dialog */}
      <Dialog open={assignOpen} onClose={() => !isAssigning && setAssignOpen(false)} maxWidth="sm" fullWidth PaperProps={{ style: { borderRadius: 12 } }}>
        <Box display="flex" alignItems="flex-start" style={{ gap: 14, padding: "20px 24px 16px", borderBottom: "1px solid #E4E7EC" }}>
          <Box style={{ width: 40, height: 40, borderRadius: "50%", flex: "none", backgroundColor: "#EEF2F3", color: "#3A4D54", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <BusinessIcon style={{ fontSize: 20 }} />
          </Box>
          <Box flex={1}>
            <Typography style={{ fontWeight: 700, fontSize: 16, color: "#101828" }}>Assign Company</Typography>
            <Typography style={{ fontSize: 13.5, color: "#667085", marginTop: 2 }}>
              Give {currentPartner.name} access to manage one or more companies.
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setAssignOpen(false)} disabled={isAssigning}><CloseIcon style={{ fontSize: 16 }} /></IconButton>
        </Box>
        <DialogContent style={{ padding: "20px 24px 4px" }}>
          <label htmlFor="assign-company-input" style={{ display: "block", fontSize: 13, fontWeight: 500, color: "#344054", marginBottom: 6 }}>
            Companies <span style={{ color: "#D92D20" }}>*</span>
          </label>
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={companyDropdown}
            getOptionLabel={(option) => option.name || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedCompanies}
            onChange={(e, value) => { setSelectedCompanies(value); setAssignError(""); }}
            getOptionDisabled={isAssignedHere}
            noOptionsText="No companies found"
            size="small"
            slotProps={{ chip: { size: "small" } }}
            renderOption={(props, option, { selected }) => {
              const { key, ...optionProps } = props;
              const assignedHere = isAssignedHere(option);
              return (
                <li key={key} {...optionProps}>
                  <Checkbox size="small" checked={selected} style={{ padding: 4, marginRight: 6, color: "#3A4D54" }} />
                  <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" style={{ gap: 12 }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 14 }}>{option.name}</span>
                    {assignedHere ? (
                      <Chip label="Already assigned" size="small" style={{ fontSize: 11, height: 20, backgroundColor: "#F2F4F7", color: "#667085", flex: "none" }} />
                    ) : option.assigned_partner ? (
                      <Chip label={option.assigned_partner.name} size="small" style={{ fontSize: 11, height: 20, backgroundColor: "#FFFAEB", color: "#B54708", flex: "none" }} />
                    ) : null}
                  </Box>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField {...params} id="assign-company-input" placeholder={selectedCompanies.length === 0 ? "Search or select companies" : ""} />
            )}
          />
          <Typography style={{ fontSize: 12.5, color: "#667085", marginTop: 6 }}>
            Companies already managed by a partner are labelled with that partner's name.
          </Typography>
          {heldSelections.length > 0 && (
            <Box mt={1.5} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "10px 12px", borderRadius: 8, backgroundColor: "#FFFAEB", border: "1px solid #FEDF89", color: "#B54708" }}>
              <InfoOutlinedIcon style={{ fontSize: 16, marginTop: 1, flex: "none" }} />
              <Typography style={{ fontSize: 13, color: "inherit" }}>
                {heldSelections.length === 1 ? (
                  <><b>{heldSelections[0].name}</b> is currently assigned to <b>{heldSelections[0].assigned_partner?.name}</b>.</>
                ) : (
                  <><b>{heldSelections.length}</b> selected companies are currently assigned to other partners.</>
                )}{" "}
                You will be asked to confirm the move.
              </Typography>
            </Box>
          )}
          {assignError && (
            <Box mt={1.5} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "10px 12px", borderRadius: 8, backgroundColor: "#FEF3F2", border: "1px solid #FECDCA", color: "#B42318" }}>
              <ErrorOutlineIcon style={{ fontSize: 16, marginTop: 1, flex: "none" }} />
              <Typography style={{ fontSize: 13, color: "inherit" }}>{assignError}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px 20px", gap: 10 }}>
          <Button
            onClick={() => setAssignOpen(false)}
            disabled={isAssigning}
            style={{ color: "#344054", fontWeight: 600, fontSize: 14, textTransform: "none", border: "1px solid #D0D5DD", borderRadius: 8, padding: "7px 18px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAssignClick}
            variant="contained"
            disabled={selectedCompanies.length === 0 || isAssigning}
            disableElevation
            style={{
              backgroundColor: selectedCompanies.length === 0 || isAssigning ? undefined : "#3A4D54",
              color: selectedCompanies.length === 0 || isAssigning ? undefined : "#fff",
              fontWeight: 600, fontSize: 14, textTransform: "none", borderRadius: 8, padding: "7px 20px", minWidth: 150,
            }}
          >
            {isAssigning && !moveConfirmOpen
              ? <CircularProgress size={18} style={{ color: "#98A2B3" }} />
              : selectedCompanies.length > 1
                ? `Assign ${selectedCompanies.length} Companies`
                : "Assign Company"}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Confirm Move Company Dialog */}
      <Dialog open={moveConfirmOpen} onClose={() => !isAssigning && setMoveConfirmOpen(false)} maxWidth="xs" fullWidth PaperProps={{ style: { borderRadius: 12 } }}>
        <Box display="flex" alignItems="flex-start" style={{ gap: 14, padding: "20px 24px 0" }}>
          <Box style={{ width: 40, height: 40, borderRadius: "50%", flex: "none", backgroundColor: "#FFFAEB", color: "#B54708", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <SwapHorizIcon style={{ fontSize: 20 }} />
          </Box>
          <Box flex={1}>
            <Typography style={{ fontWeight: 700, fontSize: 16, color: "#101828" }}>Move company?</Typography>
            <Typography style={{ fontSize: 13.5, color: "#667085", marginTop: 2 }}>
              This changes which partner manages the company.
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setMoveConfirmOpen(false)} disabled={isAssigning}><CloseIcon style={{ fontSize: 16 }} /></IconButton>
        </Box>
        <DialogContent style={{ padding: "14px 24px 4px" }}>
          {heldSelections.length === 1 ? (
            <Typography style={{ fontSize: 14, color: "#344054", lineHeight: "21px" }}>
              <b>{heldSelections[0]?.name}</b> is currently assigned to <b>{heldSelections[0]?.assigned_partner?.name}</b>.
              Move it to <b>{currentPartner.name}</b>? <b>{heldSelections[0]?.assigned_partner?.name}</b> will lose access to this company.
            </Typography>
          ) : (
            <>
              <Typography style={{ fontSize: 14, color: "#344054", lineHeight: "21px" }}>
                These companies are currently assigned to other partners:
              </Typography>
              <Box component="ul" style={{ margin: "8px 0 10px", paddingLeft: 20 }}>
                {heldSelections.map((c) => (
                  <li key={c.id} style={{ fontSize: 13.5, color: "#344054", lineHeight: "22px" }}>
                    <b>{c.name}</b> — {c.assigned_partner?.name}
                  </li>
                ))}
              </Box>
              <Typography style={{ fontSize: 14, color: "#344054", lineHeight: "21px" }}>
                Move them to <b>{currentPartner.name}</b>? The current partners will lose access to these companies.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px 20px", gap: 10 }}>
          <Button
            onClick={() => setMoveConfirmOpen(false)}
            disabled={isAssigning}
            style={{ color: "#344054", fontWeight: 600, fontSize: 14, textTransform: "none", border: "1px solid #D0D5DD", borderRadius: 8, padding: "7px 18px" }}
          >
            Back
          </Button>
          <Button
            onClick={() => doAssign(true)}
            variant="contained"
            disabled={isAssigning}
            disableElevation
            style={{
              backgroundColor: isAssigning ? undefined : "#3A4D54",
              color: isAssigning ? undefined : "#fff",
              fontWeight: 600, fontSize: 14, textTransform: "none", borderRadius: 8, padding: "7px 20px", minWidth: 150,
            }}
          >
            {isAssigning
              ? <CircularProgress size={18} style={{ color: "#98A2B3" }} />
              : heldSelections.length > 1
                ? `Move ${heldSelections.length} Companies`
                : "Move Company"}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Confirm Deactivate Dialog (no assigned companies) */}
      <Dialog open={confirmDeactivateOpen} onClose={() => !isUpdatingStatus && setConfirmDeactivateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography style={{ fontWeight: 700, fontSize: 14, color: "#B42318" }}>Deactivate Partner</Typography>
            <IconButton size="small" onClick={() => setConfirmDeactivateOpen(false)} disabled={isUpdatingStatus}><CloseIcon style={{ fontSize: 16 }} /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography style={{ fontSize: 14, color: "#344054", padding: "10px 0" }}>
            Deactivate <b>{currentPartner.name}</b>? Its portal users will no longer be able to
            sign in, and assigning companies or adding users will be disabled. You can
            reactivate this partner at any time.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeactivateOpen(false)} disabled={isUpdatingStatus} style={{ color: "#344054", fontWeight: 700, fontSize: 13 }}>
            CANCEL
          </Button>
          <Button
            onClick={handleConfirmDeactivate}
            variant="contained"
            disabled={isUpdatingStatus}
            style={{ backgroundColor: "#B42318", color: "#fff", fontWeight: 700, fontSize: 13, minWidth: 156, height: 32, borderRadius: 4, boxShadow: "0px 2px 2px rgba(34,45,48,0.15)" }}
          >
            {isUpdatingStatus ? <CircularProgress size={16} style={{ color: "#fff" }} /> : "DEACTIVATE"}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Reassign Companies Before Deactivation Dialog */}
      <Dialog open={reassignOpen} onClose={() => !isReassigning && setReassignOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography style={{ fontWeight: 700, fontSize: 14, color: "#3A4D54" }}>Reassign Companies Before Deactivating</Typography>
            <IconButton size="small" onClick={() => setReassignOpen(false)} disabled={isReassigning}><CloseIcon style={{ fontSize: 16 }} /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box bgcolor="#fff" borderRadius="4px" px="10px" py="20px">
            <Box display="flex" flexDirection="column" style={{ gap: 20 }}>
              <Typography style={{ fontSize: 14, color: "#344054" }}>
                <b>{currentPartner.name}</b> still has {partnerCompaniesTotal} assigned{" "}
                {partnerCompaniesTotal === 1 ? "company" : "companies"}. Assign{" "}
                {partnerCompaniesTotal === 1 ? "it" : "them"} to another partner before deactivating.
              </Typography>
              <Box p={1.5} style={{ backgroundColor: "#F9FAFB", borderRadius: 4 }}>
                {partnerCompanies.map((pc) => (
                  <Chip
                    key={pc.id}
                    label={pc.company_name || `Company #${pc.company_id}`}
                    size="small"
                    style={{ marginRight: 6, marginBottom: 4, fontSize: 12, backgroundColor: "#EEF4FF", color: "#3538CD" }}
                  />
                ))}
              </Box>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography style={{ fontSize: 14, color: "#344054" }}>Transfer to <span style={{ color: "#F97066" }}>*</span></Typography>
                <FormControl variant="outlined" size="small" style={{ width: 360 }}>
                  <Select
                    value={targetPartnerUuid}
                    onChange={(e) => setTargetPartnerUuid(e.target.value)}
                    displayEmpty
                    disabled={isReassigning}
                    style={{ fontSize: 14, height: 38, borderRadius: 4 }}
                  >
                    <MenuItem value="" disabled><span style={{ color: "#9CA6A9" }}>Select a partner</span></MenuItem>
                    {partnerOptions.map((p) => (
                      <MenuItem key={p.uuid} value={p.uuid}>{p.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              {partnerOptions.length === 0 && (
                <Typography style={{ fontSize: 13, color: "#F79009" }}>
                  No other active partner is available. Create or activate another partner first.
                </Typography>
              )}
              {reassignError && (
                <Typography style={{ fontSize: 13, color: "#F04438" }}>{reassignError}</Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReassignOpen(false)} disabled={isReassigning} style={{ color: "#344054", fontWeight: 700, fontSize: 13 }}>
            CANCEL
          </Button>
          <Button
            onClick={handleReassignAndDeactivate}
            variant="contained"
            disabled={!targetPartnerUuid || isReassigning}
            style={{ backgroundColor: "#3A4D54", color: "#fff", fontWeight: 700, fontSize: 13, minWidth: 220, height: 32, borderRadius: 4, boxShadow: "0px 2px 2px rgba(34,45,48,0.15)" }}
          >
            {isReassigning ? <CircularProgress size={16} style={{ color: "#fff" }} /> : "REASSIGN & DEACTIVATE"}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Add/Edit User Dialog */}
      <Dialog open={userDialogOpen} onClose={closeUserDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography style={{ fontWeight: 700, fontSize: 14, color: "#3A4D54" }}>
              {userDialogMode === "add" ? (activationUrl ? "User Created" : "Add User") : "Edit User"}
            </Typography>
            <IconButton size="small" onClick={closeUserDialog}><CloseIcon style={{ fontSize: 16 }} /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box bgcolor="#fff" borderRadius="4px" px="10px" py="20px">
            {userDialogMode === "add" && activationUrl ? (
              <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
                <Typography style={{ fontSize: 14, color: "#344054" }}>
                  An activation email has been sent to <b>{userForm.email}</b>. You can also share the link below directly.
                </Typography>
                <Box>
                  <Typography style={{ fontSize: 14, color: "#344054", marginBottom: 6 }}>Activation Link</Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={activationUrl}
                    InputProps={{ readOnly: true, style: { fontSize: 13 } }}
                  />
                  {activationExpiresAt && (
                    <Typography style={{ fontSize: 12, color: "#7C8DB5", marginTop: 4 }}>
                      Expires at {new Date(activationExpiresAt).toLocaleString()}
                    </Typography>
                  )}
                </Box>
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                  <Button
                    variant="contained"
                    onClick={copyActivationUrl}
                    style={{ backgroundColor: "#3A4D54", color: "#fff", fontWeight: 700, fontSize: 13 }}
                  >
                    COPY LINK
                  </Button>
                  {copyTooltip && (
                    <Typography style={{ fontSize: 13, color: "#12B76A" }}>{copyTooltip}</Typography>
                  )}
                </Box>
              </Box>
            ) : (
              <Box display="flex" flexDirection="column" style={{ gap: 20 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography style={{ fontSize: 14, color: "#344054" }}>Name <span style={{ color: "#F97066" }}>*</span></Typography>
                  <TextField
                    variant="outlined" size="small" placeholder="User name"
                    value={userForm.name}
                    onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                    style={{ width: 360 }}
                    inputProps={{ style: { fontSize: 14, height: 18, padding: "10px" } }}
                  />
                </Box>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography style={{ fontSize: 14, color: "#344054" }}>Email <span style={{ color: "#F97066" }}>*</span></Typography>
                  <TextField
                    variant="outlined" size="small" placeholder="User email"
                    value={userForm.email}
                    onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                    disabled={userDialogMode === "edit" && editingUserStatus !== STATUS_PENDING}
                    helperText={userDialogMode === "edit" && editingUserStatus !== STATUS_PENDING ? "Email cannot be changed after activation." : ""}
                    style={{ width: 360 }}
                    inputProps={{ style: { fontSize: 14, height: 18, padding: "10px" } }}
                  />
                </Box>
                {userDialogMode === "edit" && editingUserStatus === STATUS_ACTIVE && (
                  <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                    <Typography style={{ fontSize: 14, color: "#344054", paddingTop: 8 }}>Reset Password</Typography>
                    <Box style={{ width: 360 }}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        type="password"
                        placeholder="Leave blank to keep current password"
                        value={userForm.password}
                        onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                        inputProps={{ style: { fontSize: 14, height: 18, padding: "10px" } }}
                      />
                      <Typography style={{ fontSize: 11, color: "#7C8DB5", marginTop: 4 }}>
                        Min 8 chars with lowercase, uppercase, digit and special character.
                      </Typography>
                    </Box>
                  </Box>
                )}
                <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                  <Typography style={{ fontSize: 14, color: "#344054", paddingTop: 8 }}>Roles</Typography>
                  <Box style={{ width: 360 }}>
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      options={roleOptions}
                      getOptionLabel={(option) => option.name || ""}
                      isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                      value={roleOptions.filter((r) => userForm.roleUuids.includes(r.uuid))}
                      onChange={(e, value) => setUserForm({ ...userForm, roleUuids: value.map((r) => r.uuid) })}
                      size="small"
                      noOptionsText="No roles available"
                      renderOption={(props, option, { selected }) => {
                        const { key, ...optionProps } = props;
                        return (
                          <li key={key} {...optionProps}>
                            <Checkbox size="small" checked={selected} style={{ padding: 4, marginRight: 6, color: "#3A4D54" }} />
                            <Box>
                              <Typography style={{ fontSize: 14 }}>{option.name}</Typography>
                              {option.description && (
                                <Typography style={{ fontSize: 12, color: "#98A2B3" }}>{option.description}</Typography>
                              )}
                            </Box>
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField {...params} placeholder={userForm.roleUuids.length === 0 ? "Select roles" : ""} />
                      )}
                    />
                    <Typography style={{ fontSize: 12, color: "#98A2B3", marginTop: 4 }}>
                      Manage roles in the Access Rights page.
                    </Typography>
                  </Box>
                </Box>
                {userDialogMode === "edit" && editingUserStatus === STATUS_PENDING && (
                  <Box mt={1} p={2} style={{ backgroundColor: "#F9FAFB", borderRadius: 4 }}>
                    <Typography style={{ fontSize: 13, color: "#344054", marginBottom: 8 }}>
                      This user has not activated yet. You can regenerate and resend the activation link.
                    </Typography>
                    {activationUrl && (
                      <Box mb={1}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          value={activationUrl}
                          InputProps={{ readOnly: true, style: { fontSize: 12 } }}
                        />
                        {activationExpiresAt && (
                          <Typography style={{ fontSize: 11, color: "#7C8DB5", marginTop: 4 }}>
                            Expires at {new Date(activationExpiresAt).toLocaleString()}
                          </Typography>
                        )}
                      </Box>
                    )}
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      <Button variant="outlined" size="small" onClick={handleRegenerateInvite} style={{ fontSize: 12 }}>
                        REGENERATE ACTIVATION LINK
                      </Button>
                      {activationUrl && (
                        <Button variant="outlined" size="small" onClick={copyActivationUrl} style={{ fontSize: 12 }}>
                          COPY
                        </Button>
                      )}
                      {copyTooltip && (
                        <Typography style={{ fontSize: 12, color: "#12B76A" }}>{copyTooltip}</Typography>
                      )}
                    </Box>
                  </Box>
                )}
                {userFormError && (
                  <Typography style={{ fontSize: 13, color: "#F04438" }}>{userFormError}</Typography>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          {userDialogMode === "add" && activationUrl ? (
            <Button
              onClick={closeUserDialog}
              variant="contained"
              style={{ backgroundColor: "#3A4D54", color: "#fff", fontWeight: 700, fontSize: 13, minWidth: 156, height: 32, borderRadius: 4 }}
            >
              DONE
            </Button>
          ) : (
            <Button
              onClick={handleUserSubmit}
              variant="contained"
              disabled={!userForm.name || !userForm.email}
              style={{ backgroundColor: "#3A4D54", color: "#fff", fontWeight: 700, fontSize: 13, minWidth: 156, height: 32, borderRadius: 4, boxShadow: "0px 2px 2px rgba(34,45,48,0.15)" }}
            >
              {userDialogMode === "add" ? "CREATE USER" : "CONFIRM"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <Snackbar
        open={!!statusSnack}
        autoHideDuration={4000}
        onClose={() => setStatusSnack("")}
        message={statusSnack}
      />
    </Box>
  );
}
